import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/modern/utils";

/** Default copy for Before / During / After marketing temporal tags. */
const TEMPORAL_DESCRIPTIONS: Record<string, string> = {
  Before: "Pre-construction / inspection state",
  During: "Active work in progress",
  After: "Post-completion / finished state",
};

export type TemporalPhaseTag = {
  id: number;
  label: string;
  /** Overrides label-based default description when set. */
  description?: string | null;
};

type Props = {
  tags: TemporalPhaseTag[];
  value: number | null;
  onChange: (tagId: number) => void;
  disabled?: boolean;
  /** Shown when `tags` is empty. Pass `null` to render nothing. */
  emptyMessage?: string | null;
};

function tagDescription(tag: TemporalPhaseTag): string {
  return tag.description || TEMPORAL_DESCRIPTIONS[tag.label] || "";
}

/**
 * Compact Before / During / After segmented control (button.segmented tokens).
 * Used by File Detail Promote Classify and Promote Surface accept fields.
 */
export function MarketingTemporalPhasePicker({
  tags,
  value,
  onChange,
  disabled,
  emptyMessage = "No temporal tags available.",
}: Props) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  if (tags.length === 0) {
    if (emptyMessage == null) return null;
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  const revealedId = hoveredId ?? value;
  const revealed = tags.find((t) => t.id === revealedId) ?? null;
  const revealedDescription = revealed ? tagDescription(revealed) : "";

  return (
    <div
      className={cn("w-full", disabled && "pointer-events-none opacity-50")}
      onMouseLeave={() => setHoveredId(null)}
    >
      <div
        className="flex w-full gap-0.5 rounded-full border border-input bg-muted p-0.5"
        role="radiogroup"
        aria-label="Temporal phase"
      >
        {tags.map((tag) => {
          const selected = value === tag.id;
          const description = tagDescription(tag);

          return (
            <button
              key={tag.id}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={description ? `${tag.label}: ${description}` : tag.label}
              disabled={disabled}
              onClick={() => onChange(tag.id)}
              onMouseEnter={() => setHoveredId(tag.id)}
              onFocus={() => setHoveredId(tag.id)}
              onBlur={() => setHoveredId((current) => (current === tag.id ? null : current))}
              className={cn(
                "h-8 min-w-0 flex-1 rounded-full px-2 text-sm font-normal outline-none transition-colors",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                selected
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tag.label}
            </button>
          );
        })}
      </div>

      <div className="mt-1 min-h-4 px-1 text-center">
        <AnimatePresence initial={false} mode="wait">
          {revealed && revealedDescription ? (
            <motion.p
              key={revealed.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="text-xs leading-snug text-muted-foreground"
            >
              {revealedDescription}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
